'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { useCallback, useMemo } from 'react';

import { AuthMutation, AuthQueryKey, AuthQueryOptions } from '@/api';
import { useAuthStore } from '@/stores/AuthStore';

import { transformCartResponse } from '../_utils/transformCart';

export const useMarketCart = () => {
  const queryClient = useQueryClient();
  const isLogin = useAuthStore((state) => !!state.isLogin);

  const queryKey = AuthQueryKey['get:/wello/v2/auth/local-market/cart-items']();

  // 장바구니 목록 조회
  const cartQuery = useQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/local-market/cart-items'](),
    enabled: isLogin,
    staleTime: 1000 * 60 * 3,
    gcTime: 1000 * 60 * 60,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInvalidate = useCallback(
    debounce(() => {
      queryClient.invalidateQueries({ queryKey });
    }, 300),
    [queryClient, queryKey],
  );

  // 공통 mutation 옵션
  const mutationOptions = {
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previousCart = queryClient.getQueryData(queryKey);

      return { previousCart };
    },
    onError: (
      _error: unknown,
      _variables: unknown,
      context: { previousCart: unknown } | undefined,
    ) => {
      if (context?.previousCart) {
        queryClient.setQueryData(queryKey, context.previousCart);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  };

  // 장바구니 추가
  const addMutation = useMutation({
    ...AuthMutation['post:/wello/v2/auth/local-market/cart-items'](),
    ...mutationOptions,
  });

  // 수량 수정
  const updateQuantityMutation = useMutation({
    ...AuthMutation[
      'put:/wello/v2/auth/local-market/cart-items/{cartItemId}/quantity'
    ](),
    ...mutationOptions,
    onSettled: debouncedInvalidate,
  });

  // 옵션 수정
  const updateOptionsMutation = useMutation({
    ...AuthMutation[
      'put:/wello/v2/auth/local-market/cart-items/{cartItemId}/option'
    ](),
    ...mutationOptions,
  });

  // 장바구니 삭제
  const deleteMutation = useMutation({
    ...AuthMutation['delete:/wello/v2/auth/local-market/cart-items'](),
    ...mutationOptions,
  });

  const shops = useMemo(
    () => transformCartResponse(cartQuery.data?.context),
    [cartQuery.data],
  );

  const updateQuantity = async (cartItemId: string, quantity: number) => {
    return updateQuantityMutation.mutateAsync({
      path: { cartItemId },
      body: { quantity },
    });
  };

  const updateOptions = async (
    cartItemId: string,
    productOptionItemIds: string[],
  ) => {
    return updateOptionsMutation.mutateAsync({
      path: { cartItemId },
      body: { productOptionItemIds },
    });
  };

  const addItem = async (params: {
    mallProductId: string;
    mallVendorId: string;
    options?: Array<{ productOptionItemId: string; quantity?: number }>;
    cartType?: 'ON_CART' | 'INSTANT';
  }) => {
    return addMutation.mutateAsync({
      body: params,
    });
  };

  const deleteItems = async (cartItemIds: string[]) => {
    return deleteMutation.mutateAsync({
      body: { cartItemIds },
    });
  };

  return {
    shops,
    totalCount: shops.reduce((sum, shop) => sum + shop.products.length, 0),
    isLoading: cartQuery.isLoading,
    isFetching: cartQuery.isFetching,

    addItem,
    isAdding: addMutation.isPending,

    updateQuantity,
    isUpdatingQuantity: updateQuantityMutation.isPending,

    updateOptions,
    isUpdatingOptions: updateOptionsMutation.isPending,

    deleteItems,
    isDeleting: deleteMutation.isPending,

    refetch: cartQuery.refetch,
  };
};
